import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";

const SubscriptionContext = createContext(null);

// Feature-to-route mapping for navigation gating
const ROUTE_FEATURE_MAP = {
  "/speech": "speech_training",
  "/accent": "accent_training",
  "/conversation": "roleplay",
  "/cases": "case_studies",
  "/solver": "problem_solver",
  "/offline": "offline_mode",
};

// Tier hierarchy for comparison
const TIER_ORDER = { free: 0, starter: 1, pro: 2, family: 3, school: 4 };

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within SubscriptionProvider");
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/subscription/status`, {
        withCredentials: true,
      });
      setSubscription(res.data);
    } catch {
      setSubscription({
        subscription: { tier: "free", status: "active" },
        feature_access: {},
        usage: {},
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const tier = subscription?.subscription?.tier || "free";
  const isTrial = subscription?.is_trial || false;
  const trialEnd = subscription?.trial_end || null;
  const daysRemaining = subscription?.days_remaining ?? null;
  const trialExpired = subscription?.trial_expired || false;

  const canAccess = useCallback(
    (featureName) => {
      if (!subscription) return true; // default allow during loading
      const access = subscription?.feature_access || {};
      return access[featureName] !== false;
    },
    [subscription]
  );

  const canAccessRoute = useCallback(
    (path) => {
      const feature = ROUTE_FEATURE_MAP[path];
      if (!feature) return true; // no gating for unmapped routes
      return canAccess(feature);
    },
    [canAccess]
  );

  const getRequiredTier = useCallback((featureName) => {
    const FEATURE_MIN_TIER = {
      adaptive_learning: "starter",
      progress_tracking: "starter",
      problem_solver: "starter",
      offline_mode: "starter",
      tutor_chat_unlimited: "pro",
      case_studies: "pro",
      voice_tutor: "pro",
      speech_training: "pro",
      accent_training: "pro",
      roleplay: "pro",
      stem_masterclass: "pro",
      flashcards_unlimited: "pro",
      parent_dashboard: "family",
      teacher_dashboard: "school_basic",
      ai_lesson_planner: "school_pro",
    };
    return FEATURE_MIN_TIER[featureName] || "free";
  }, []);

  const getFeatureForRoute = useCallback((path) => {
    return ROUTE_FEATURE_MAP[path] || null;
  }, []);

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        tier,
        isTrial,
        trialEnd,
        daysRemaining,
        trialExpired,
        loading,
        canAccess,
        canAccessRoute,
        getRequiredTier,
        getFeatureForRoute,
        refresh: fetchSubscription,
        TIER_ORDER,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};
